import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import type { NewsArticle } from './schema';
import { KindCell } from './components/cells/KindCell';
import { PublishedCell } from './components/cells/PublishedCell';
import { RowActions } from './components/cells/RowActions';
import { SlugCell } from './components/cells/SlugCell';
import { StatusBadge } from './components/cells/StatusBadge';
import { TitleCell } from './components/cells/TitleCell';

export interface NewsArticleRowActions {
  onEdit: (row: NewsArticle) => void;
  onDelete: (row: NewsArticle) => void;
}

// `colId` on sortable columns matches the API's sortBy key. NewsArticlesGrid
// reads it from the column state and sends it as `sortBy`.
export function buildNewsArticlesColumns(
  actions: NewsArticleRowActions,
): ColDef<NewsArticle>[] {
  return [
    {
      colId: 'title',
      field: 'title',
      headerName: 'Title',
      flex: 2,
      minWidth: 240,
      sortable: true,
      pinned: 'left',
      lockPinned: true,
      cellRenderer: TitleCell,
    },
    {
      field: 'slug',
      headerName: 'Slug',
      flex: 1,
      minWidth: 180,
      cellRenderer: SlugCell,
    },
    {
      field: 'kind',
      headerName: 'Kind',
      // 140px gives the ARTICLE pill (icon + 7-letter label + padding) enough
      // room to render without ag-grid's cell-overflow ellipsis kicking in.
      // VIDEO is shorter and would fit in 120, but the column needs to size
      // for the wider option.
      width: 140,
      cellRenderer: KindCell,
    },
    {
      colId: 'status',
      field: 'status',
      headerName: 'Status',
      width: 140,
      sortable: true,
      cellRenderer: StatusBadge,
    },
    {
      colId: 'published',
      field: 'publishedAt',
      headerName: 'Published',
      width: 200,
      sortable: true,
      cellRenderer: PublishedCell,
    },
    {
      headerName: 'Actions',
      width: 130,
      sortable: false,
      filter: false,
      pinned: 'right',
      cellRenderer: (p: ICellRendererParams<NewsArticle>) =>
        p.data ? <RowActions row={p.data} {...actions} /> : null,
    },
  ];
}
